import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight as ChevronRightIcon, Plus, Shield, X, type LucideIcon } from "lucide-react";
import type { PendingInvitation } from "@multi-chat/shared";
import { isApiError } from "../../../api/client.js";
import { acceptMyInvitation } from "../../../api/team-api.js";
import {
  AuthAlert,
  AuthEntryHeader,
  AuthEntryPresenceView,
  AuthEntryShell,
} from "../../auth/entry/index.js";
import {
  authEntryChooserViewMotion,
  authEntryCreatePanelMotion,
} from "../../auth/entry/auth-entry-motion.js";
import {
  AUTH_ENTRY_CARD,
  AUTH_ENTRY_ICON_WELL,
  AUTH_ENTRY_ICON_WELL_ACTIVE,
  AUTH_ENTRY_PAIRED_ACTION,
  AUTH_ENTRY_PRIMARY_ACTION,
  AUTH_ENTRY_ROW_SUBTITLE,
  AUTH_ENTRY_ROW_TITLE,
  AUTH_ENTRY_SECONDARY_ACTION,
  AUTH_ENTRY_SECTION_LABEL,
} from "../../auth/entry/auth-entry-styles.js";
import { useAuth } from "../../auth/auth-context.js";
import { FeedBotLogo } from "../../../components/brand/FeedBotLogo.js";
import { Button } from "../../../components/ui/button/index.js";
import { AnimatePresence, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { useOrganizations } from "../organization-context.js";
import { CreateOrganizationForm } from "../CreateOrganizationForm.js";
import { groupChooserMemberships, membershipRoleOf } from "../organization-selection.js";
import { markWorkspaceEntered } from "../workspace-entry.js";
import {
  OrganizationIcon,
  organizationIconForRole,
  SUPER_ADMIN_ICON_TONE,
} from "../OrganizationIcon.js";
import { PendingInvitationRow } from "../PendingInvitationRow.js";

interface OrganizationSelectPageProps {
  onContinue: () => void;
}

const SUPER_ADMIN_OPTION = "super-admin";

/** Chooser option key: an organization id, or the Super Admin workspace. */
type ChooserOptionKey = number | typeof SUPER_ADMIN_OPTION;

interface ChooserOption {
  key: ChooserOptionKey;
  name: string;
}

function optionDomId(key: ChooserOptionKey): string {
  return key === SUPER_ADMIN_OPTION ? "organization-option-super-admin" : `organization-option-${key}`;
}

function OrganizationSelectCard({
  optionKey,
  title,
  subtitle,
  suspended = false,
  icon,
  iconTone,
  testId,
  selected,
  onSelect,
  tabIndex,
  onFocus,
}: {
  optionKey: ChooserOptionKey;
  title: string;
  subtitle: string;
  suspended?: boolean;
  icon: LucideIcon;
  /** Fixed identity tone (owner crown, Super Admin shield); otherwise the well follows selection. */
  iconTone?: string;
  testId: string;
  selected: boolean;
  onSelect: () => void;
  tabIndex: number;
  onFocus: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      id={optionDomId(optionKey)}
      role="option"
      aria-selected={selected}
      tabIndex={tabIndex}
      onClick={onSelect}
      onFocus={onFocus}
      data-testid={testId}
      data-selected={selected ? "true" : "false"}
      className={cn(
        "group flex w-full min-w-0 items-center gap-3 rounded-[12px] border px-3.5 py-3 text-start transition-[border-color,background-color,box-shadow] duration-150 ease-in-out",
        "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/35",
        selected
          ? "border-brand bg-[#f3faf7] shadow-[0_0_0_3px_#55bca61f]"
          : "border-[#dce7df] bg-white hover:border-[#b4cec1] hover:bg-[#fcfefd]",
      )}
    >
      <OrganizationIcon
        size="lg"
        icon={icon}
        className={cn(
          "rounded-[10px]",
          iconTone ?? (selected ? AUTH_ENTRY_ICON_WELL_ACTIVE : AUTH_ENTRY_ICON_WELL),
        )}
      />
      <span className="min-w-0 flex-1">
        <span className={AUTH_ENTRY_ROW_TITLE}>
          <bdi>{title}</bdi>
        </span>
        <span className={AUTH_ENTRY_ROW_SUBTITLE}>{subtitle}</span>
        {suspended ? (
          <span className="block text-[11px] leading-4 font-[700] text-warning">
            {t("organization.suspended.badge")}
          </span>
        ) : null}
      </span>
      <ChevronRightIcon
        size={18}
        className={cn(
          "shrink-0 transition-opacity duration-150 rtl:rotate-180",
          selected
            ? "text-brand-primary opacity-100"
            : "text-[#9aaba3] opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
        )}
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * Workspace chooser: the caller's own memberships, plus the Super Admin
 * workspace for an active Platform Admin. Platform Admin managed organizations
 * stay in the in-app switcher and are never listed here.
 */
export function OrganizationSelectPage({ onContinue }: OrganizationSelectPageProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { phase, refreshAfterAuth } = useAuth();
  const { select, selectCreatedOrganization, platformAdmin } = useOrganizations();
  const memberships = phase.kind === "authenticated" ? phase.user.organizations : [];
  const pendingInvitations =
    phase.kind === "authenticated" ? phase.user.pendingInvitations : [];
  // Single source of truth for chooser selection — never preselect on mount.
  const [chosenKey, setChosenKey] = useState<ChooserOptionKey | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [joiningId, setJoiningId] = useState<number | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const ownsOrganization = memberships.some(
    (organization) => membershipRoleOf(organization) === "OWNER",
  );
  const listRef = useRef<HTMLDivElement>(null);
  const hasSelection = chosenKey !== null;
  const reduceMotion = useReducedMotion() ?? false;
  const createPanelMotion = authEntryCreatePanelMotion(reduceMotion);
  const chooserViewMotion = authEntryChooserViewMotion(reduceMotion);
  const restoreCreateFocus = useRef(false);

  // The Create trigger remounts only after the card's exit animation finishes.
  const createTriggerRef = useCallback((node: HTMLButtonElement | null) => {
    if (node && restoreCreateFocus.current) {
      restoreCreateFocus.current = false;
      node.focus();
    }
  }, []);

  function closeCreate(): void {
    restoreCreateFocus.current = true;
    setShowCreate(false);
  }

  const grouped = groupChooserMemberships(memberships);
  const superAdminLabel = t("superAdmin.badge");
  const options: ChooserOption[] = [
    ...(grouped.owned ? [grouped.owned] : []),
    ...grouped.members,
  ].map((organization) => ({ key: organization.id, name: organization.name }));
  if (platformAdmin) {
    options.push({ key: SUPER_ADMIN_OPTION, name: superAdminLabel });
  }
  const indexOf = (key: ChooserOptionKey): number => options.findIndex((option) => option.key === key);

  const enter = useCallback(
    (key: ChooserOptionKey) => {
      if (key === SUPER_ADMIN_OPTION) {
        navigate("/super-admin");
        return;
      }
      select(key);
      onContinue();
    },
    [navigate, onContinue, select],
  );

  const joinInvitation = useCallback(
    async (invitation: PendingInvitation) => {
      setJoinError(null);
      setJoiningId(invitation.invitationId);
      try {
        const accepted = await acceptMyInvitation(invitation.invitationId);
        selectCreatedOrganization(accepted.organizationId);
        markWorkspaceEntered();
        refreshAfterAuth();
        onContinue();
      } catch (caught) {
        setJoinError(isApiError(caught) ? caught.message : t("team.invite.acceptError"));
      } finally {
        setJoiningId(null);
      }
    },
    [onContinue, refreshAfterAuth, selectCreatedOrganization, t],
  );

  const optionCount = options.length;
  const focusedKey = focusedIndex !== null ? options[focusedIndex]?.key : undefined;

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (showCreate || optionCount === 0) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex((index) => (index === null ? 0 : Math.min(index + 1, optionCount - 1)));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex((index) => (index === null ? 0 : Math.max(index - 1, 0)));
      } else if (event.key === "Enter" && focusedKey !== undefined) {
        event.preventDefault();
        setChosenKey(focusedKey);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusedKey, optionCount, showCreate]);

  useEffect(() => {
    if (focusedIndex === null) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-org-index="${focusedIndex}"] [role="option"]`);
    node?.focus();
  }, [focusedIndex]);

  const focusHintName =
    focusedIndex !== null
      ? options[focusedIndex]?.name
      : chosenKey !== null
        ? options.find((option) => option.key === chosenKey)?.name
        : undefined;

  function choose(key: ChooserOptionKey): void {
    setChosenKey(key);
    setFocusedIndex(indexOf(key));
  }

  function membershipCard(organization: (typeof memberships)[number]): React.JSX.Element {
    const index = indexOf(organization.id);
    const role = membershipRoleOf(organization);
    return (
      <div key={organization.id} data-org-index={index}>
        <OrganizationSelectCard
          optionKey={organization.id}
          title={organization.name}
          subtitle={t(`organization.role.${role.toLowerCase()}`)}
          icon={organizationIconForRole(role)}
          iconTone={role === "OWNER" ? AUTH_ENTRY_ICON_WELL_ACTIVE : undefined}
          suspended={organization.status === "SUSPENDED"}
          testId={`organization-select-card-${organization.id}`}
          selected={chosenKey === organization.id}
          tabIndex={index === 0 ? 0 : -1}
          onFocus={() => setFocusedIndex(index)}
          onSelect={() => choose(organization.id)}
        />
      </div>
    );
  }

  const superAdminIndex = indexOf(SUPER_ADMIN_OPTION);

  return (
    <AuthEntryShell fitViewport>
      <AnimatePresence mode="wait" initial={false}>
      {showCreate ? (
        <AuthEntryPresenceView key="create" className="min-w-0" variants={createPanelMotion}>
        <section
          className={cn(AUTH_ENTRY_CARD, "relative grid min-w-0 gap-6 p-5 sm:p-7")}
          data-testid="organization-select-create-panel"
        >
          <button
            type="button"
            className="absolute end-3 top-3 inline-flex size-8 items-center justify-center rounded-[8px] border-0 bg-transparent text-[#82958b] transition-colors hover:bg-[#f3f7f6] hover:text-[#17302e] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/35"
            aria-label={t("common.close")}
            data-testid="organization-select-create-close"
            onClick={closeCreate}
          >
            <X size={16} aria-hidden="true" />
          </button>
          <FeedBotLogo
            tone="onLight"
            animated
            label={t("auth.wordmarkLabel")}
            className="w-[128px] justify-self-start"
          />
          <header className="grid gap-2 text-start">
            <h1 className="m-0 text-[24px] leading-[1.2] font-[700] tracking-[-0.05em] text-[#12372e] wrap-anywhere md:text-[27px]">
              {t("organization.select.createTitle")}
            </h1>
            <p className="m-0 text-sm leading-normal text-[#82958b] wrap-anywhere">
              {t("organization.create.subtitle")}
            </p>
          </header>
          <CreateOrganizationForm
            submitLabel={t("organization.create.submit")}
            autoFocus
            onCreated={(organizationId) => {
              selectCreatedOrganization(organizationId);
              markWorkspaceEntered();
              refreshAfterAuth();
              onContinue();
            }}
            secondaryAction={
              <Button
                type="button"
                variant="outline"
                className={cn(AUTH_ENTRY_SECONDARY_ACTION, AUTH_ENTRY_PAIRED_ACTION)}
                data-testid="organization-select-create-cancel"
                onClick={closeCreate}
              >
                {t("organization.select.cancelCreate")}
              </Button>
            }
          />
        </section>
        </AuthEntryPresenceView>
      ) : (
        <AuthEntryPresenceView
          key="chooser"
          className="grid min-w-0 gap-5 fit-desktop:flex fit-desktop:min-h-0 fit-desktop:flex-col short-desktop:gap-4"
          variants={chooserViewMotion}
        >
          <AuthEntryHeader
            eyebrow={t("organization.select.eyebrow")}
            title={t("organization.select.title")}
            subtitle={t("organization.select.subtitle")}
          />
          {options.length > 0 ? (
          <div
            ref={listRef}
            role="listbox"
            aria-label={t("organization.select.listLabel")}
            aria-activedescendant={chosenKey !== null ? optionDomId(chosenKey) : undefined}
            className={cn(
              "-my-1 -ms-1 -me-[14px] grid max-h-[min(24rem,50dvh)] min-h-0 content-start gap-4 overflow-y-auto overscroll-contain py-1 ps-1 pe-2 [scrollbar-gutter:stable]",
              "[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:my-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#b7e3d3] [&::-webkit-scrollbar-thumb:hover]:bg-[#6cc7ae]",
              "no-webkit-scrollbar:[scrollbar-color:#b7e3d3_transparent] no-webkit-scrollbar:[scrollbar-width:thin]",
            )}
            data-testid="organization-select-list"
          >
            {grouped.owned ? (
              <section className="grid gap-2" data-testid="organization-select-owned">
                <p className={AUTH_ENTRY_SECTION_LABEL}>{t("organization.select.myOrganization")}</p>
                {membershipCard(grouped.owned)}
              </section>
            ) : null}
            {grouped.members.length > 0 ? (
              <section className="grid gap-2" data-testid="organization-select-members">
                <p className={AUTH_ENTRY_SECTION_LABEL}>
                  {t("organization.select.memberOrganizations")}
                </p>
                <div className="grid gap-2">{grouped.members.map(membershipCard)}</div>
              </section>
            ) : null}
            {platformAdmin ? (
              <section className="grid gap-2" data-testid="organization-select-platform">
                <div data-org-index={superAdminIndex}>
                  <OrganizationSelectCard
                    optionKey={SUPER_ADMIN_OPTION}
                    title={superAdminLabel}
                    subtitle={t("organization.switcher.platform")}
                    icon={Shield}
                    iconTone={SUPER_ADMIN_ICON_TONE}
                    testId="organization-select-super-admin"
                    selected={chosenKey === SUPER_ADMIN_OPTION}
                    tabIndex={superAdminIndex === 0 ? 0 : -1}
                    onFocus={() => setFocusedIndex(superAdminIndex)}
                    onSelect={() => choose(SUPER_ADMIN_OPTION)}
                  />
                </div>
              </section>
            ) : null}
          </div>
          ) : null}

          {pendingInvitations.length > 0 ? (
            <section className="grid gap-2" data-testid="organization-select-invitations">
              <p className={AUTH_ENTRY_SECTION_LABEL}>{t("organization.select.pendingInvitations")}</p>
              <div className="grid gap-2">
                {pendingInvitations.map((invitation) => (
                  <PendingInvitationRow
                    key={invitation.invitationId}
                    organizationName={invitation.organizationName}
                    roleLabel={t(`organization.role.${invitation.role.toLowerCase()}`)}
                    actionLabel={
                      joiningId === invitation.invitationId
                        ? t("team.invite.accepting")
                        : t("team.invite.accept")
                    }
                    disabled={joiningId !== null}
                    testId={`organization-select-invitation-${invitation.invitationId}`}
                    actionTestId={`organization-select-join-${invitation.invitationId}`}
                    onAccept={() => void joinInvitation(invitation)}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {joinError ? <AuthAlert>{joinError}</AuthAlert> : null}

          <div className="grid gap-2.5 border-t border-[#d5e5dc] pt-5" data-testid="organization-select-actions">
            {options.length > 0 ? (
              <Button
                type="button"
                className={AUTH_ENTRY_PRIMARY_ACTION}
                disabled={!hasSelection}
                data-testid="organization-select-continue"
                onClick={() => {
                  if (chosenKey !== null) enter(chosenKey);
                }}
              >
                {t("organization.select.continuePlain")}
              </Button>
            ) : null}

            {ownsOrganization || platformAdmin ? null : (
              <Button
                type="button"
                variant={options.length > 0 ? "ghost" : "default"}
                className={
                  options.length > 0
                    ? "h-10 w-full rounded-[10px] text-[14px] font-[700] text-[#47665a] not-disabled:hover:bg-brand-mint not-disabled:hover:text-brand-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/35"
                    : AUTH_ENTRY_PRIMARY_ACTION
                }
                ref={createTriggerRef}
                data-testid="organization-select-create"
                onClick={() => setShowCreate(true)}
              >
                <Plus size={16} aria-hidden="true" data-testid="organization-select-create-icon" />
                <span>{t("organization.select.createAnother")}</span>
              </Button>
            )}
          </div>
        </AuthEntryPresenceView>
      )}
      </AnimatePresence>

      {focusHintName && !showCreate ? (
        <p className="sr-only" aria-live="polite">
          {t("organization.select.focusHint", { name: focusHintName })}
        </p>
      ) : null}
    </AuthEntryShell>
  );
}
